import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

export const useQueryParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const getQueryParams = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of urlParams.entries()) {
      params[key] = value;
    }
    return params;
  }, [location.search]);

  const navigateWithParams = useCallback((path, additionalParams = {}) => {
    const currentParams = getQueryParams();
    const newParams = { ...currentParams, ...additionalParams };
    
    // Build query string
    const queryString = Object.keys(newParams)
      .filter(key => newParams[key] !== undefined && newParams[key] !== null)
      .map(key => `${key}=${encodeURIComponent(newParams[key])}`)
      .join('&');
    
    const newPath = queryString ? `${path}?${queryString}` : path;
    navigate(newPath);
  }, [navigate, getQueryParams]);

  const navigateWithoutParam = useCallback((path, paramToRemove) => {
    const currentParams = getQueryParams();
    const newParams = { ...currentParams };
    delete newParams[paramToRemove];
    
    // Build query string
    const queryString = Object.keys(newParams)
      .filter(key => newParams[key] !== undefined && newParams[key] !== null)
      .map(key => `${key}=${encodeURIComponent(newParams[key])}`)
      .join('&');
    
    const newPath = queryString ? `${path}?${queryString}` : path;
    navigate(newPath);
  }, [navigate, getQueryParams]);

  const hasParam = useCallback((paramName) => {
    const params = getQueryParams();
    return params.hasOwnProperty(paramName);
  }, [getQueryParams]);

  const getParam = useCallback((paramName) => {
    const params = getQueryParams();
    return params[paramName];
  }, [getQueryParams]);

  return {
    getQueryParams,
    navigateWithParams,
    navigateWithoutParam,
    hasParam,
    getParam,
    currentSearch: location.search
  };
}; 